<template>
  <form @submit.prevent="submit" autocomplete="off">
    <b-row>
      <b-col sm="6">
        <b-form-group
          :label="this.$t('Account Name')"
          label-for="accountname"
          :state="$validateState($v.form.accountname)"
        >
          <b-form-input
            autofocus
            data-test-id="modal-customer-accountname"
            :state="$validateState($v.form.accountname)"
            id="accountname"
            @blur="setValidationInput('accountname', $event.target.value)"
            :placeholder="$t('Business Name')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.accountname.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group
          :label="this.$t('Email')"
          label-for="email"
          :state="$validateState($v.form.email)"
        >
          <b-form-input
            data-test-id="modal-customer-email"
            :state="$validateState($v.form.email)"
            id="email"
            @blur="setValidationInput('email', $event.target.value)"
            :placeholder="$t('e.g. name@company.com')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.email.required">
              {{ $t("This is a required field.") }}
            </p>
            <p v-if="!$v.form.email.email && $v.form.email.required">
              {{ $t("Please enter a valid email") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group
          :label="this.$t('Description (optional)')"
          label-for="description"
          :state="$validateState($v.form.description)"
        >
          <b-form-input
            data-test-id="modal-customer-description"
            :state="$validateState($v.form.description)"
            id="description"
            v-model="form.description"
            @blur="setValidationInput('description', $event.target.value)"
            :placeholder="$t('e.g. Marketing')"
          />
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group
          :label="this.$t('Password')"
          label-for="password"
          :state="$validateState($v.form.password)"
        >
          <b-form-input
            data-test-id="modal-customer-password"
            :state="$validateState($v.form.password)"
            id="password"
            type="password"
            @blur="setValidationInput('password', $event.target.value)"
            :placeholder="$t('Password')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.password.required">
              {{ $t("This is a required field.") }}
            </p>
            <p
              v-if="
                $v.form.password.$dirty &&
                $v.form.password.required &&
                !$v.form.password.minLength
              "
            >
              {{
                $t("X must be at least X characters long.", {
                  field: $t("Password"),
                  length: $v.form.password.$params.minLength.min,
                })
              }}
            </p>
            <p
              v-if="
                $v.form.password.$dirty &&
                !$v.form.password.containsSpecial &&
                $v.form.password.minLength &&
                $v.form.password.required
              "
            >
              {{ $t("Password must contain a letter and a number or symbol") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group
          :label="this.$t('License')"
          label-for="license"
          :state="$validateState($v.form.license)"
        >
          <b-form-input
            data-test-id="modal-customer-license"
            :state="$validateState($v.form.license)"
            id="license"
            @blur="setValidationInput('license', $event.target.value)"
            :placeholder="$t('License')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.license.required">
              {{ $t("This is a required field.") }}
            </p>
            <p v-if="$v.form.license.required && !$v.form.license.maxLength">
              {{
                $t("X must be at most X characters long.", {
                  field: $t("License"),
                  length: 64,
                })
              }}
            </p>
          </template>
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group
          :label="this.$t('License Count')"
          label-for="license_count"
          :state="$validateState($v.form.license_count)"
        >
          <b-form-input
            data-test-id="modal-customer-license-count"
            :state="$validateState($v.form.license_count)"
            id="license_count"
            @blur="setValidationInput('license_count', $event.target.value)"
            :placeholder="$t('License Count')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.license_count.numeric">
              {{ $t("License Count must be numeric.") }}
            </p>
          </template>
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group :label="this.$t('Content Category Protection Level')">
          <b-form-select
            id="protection-category"
            v-model="form.policy_level"
            @change="setValidationInput('policy_level', $event)"
            :state="$validateState($v.form.policy_level)"
          >
            <option
              v-for="(option, idx) in categoryProtectionList"
              :key="idx"
              :value="option.value"
              :title="option.title"
              :disabled="option.disabled"
            >
              {{ option.text }}
            </option></b-form-select
          >
        </b-form-group>
      </b-col>
      <b-col sm="6">
        <b-form-group :label="this.$t('Timezone')" label-for="timezone">
          <b-form-select
            id="timezone"
            v-model="form.timezone"
            @change="setValidationInput('timezone', $event)"
            :state="$validateState($v.form.timezone)"
            :options="timezoneList"
          ></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>
  </form>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  required,
  numeric,
  minLength,
  email,
  maxLength,
} from "vuelidate/lib/validators";

export default {
  data() {
    return {
      licenses: [{ value: "test", text: "API dependency" }],
      isAvilable: true,
      form: {
        policy_level: null,
        accountname: null,
        email: null,
        description: "",
        password: null,
        license: null,
        license_count: 0,
        timezone: null,
        msp_id: null,
      },
    };
  },
  validations: {
    form: {
      accountname: {
        required,
      },
      email: {
        required,
        email,
      },
      license: {
        required,
        maxLength: maxLength(64),
      },
      license_count: {
        numeric,
        minValue: 0,
        maxValue: 2147483647,
      },
      policy_level: {
        required,
      },
      description: {},
      timezone: {},
      password: {
        required,
        minLength: minLength(8),
        containsSpecial(password) {
          return this.$validPassword(password);
        },
      },
      msp_id: {},
    },
  },
  methods: {
    ...mapMutations("modal", ["userAction", "showModal"]),
    ...mapActions("users", ["createCustomerAccount"]),
    ...mapActions("defaultPolicy", ["fetchDefaultFilteringPolicy"]),
    ...mapActions("toast", ["displayToast"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          if (!this.form.timezone) delete this.form.timezone;
          if (this.form.license_count == "") this.form.license_count = 0;
          const response = await this.createCustomerAccount({
            customer: this.form,
          });

          if (response.code === 200) {
            this.displayToast({
              title: this.$t("Success"),
              message: this.$t("Created"),
              duration: 2000,
              variant: "success",
            });
            this.showModal(false);
          }
        } catch (err) {
          /* istanbul ignore next */
          console.error(err); // eslint-disable-line no-console
        }
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["mspId"]),
    ...mapGetters("defaultPolicy", ["defaultFilteringPolicy"]),
    timezoneList() {
      return [
        { value: null, text: this.$t("Please select"), disabled: true },
        ...this.$timezones,
      ];
    },
    categoryProtectionList() {
      return [
        { value: null, text: this.$t("Please select"), disabled: true },
        { value: "L", text: this.$t("Essential") },
        { value: "M", text: this.$t("Enhanced") },
        { value: "H", text: this.$t("Optimum") },
        {
          value: "C",
          text: this.$t("Custom"),
          disabled: !this.isAvilable,
          title: this.isAvilable
            ? ""
            : "The Global Default Policy must have Custom content filtering selected",
        },
      ];
    },
  },
  async created() {
    this.form.msp_id = this.mspId;
    await this.fetchDefaultFilteringPolicy({ mspId: this.mspId });
    this.isAvilable = this.defaultFilteringPolicy.level === "C" ? true : false;
    this.userAction(this.submit);
  },
};
</script>
